import React, { useState, useCallback } from 'react';
import {
  Card,
  Upload,
  Button,
  message,
  Table,
  Typography,
  Space,
  Alert,
  Progress,
  Row,
  Col,
  Statistic,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  ImportOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { importMinvuData } from '../../api/administratorLeads';
import type { AdministratorLead } from '../../types/administratorLeads';

const { Title, Text } = Typography;

interface MinvuRecord {
  id_administrador: number;
  rut: string;
  nombre_completo: string;
  email: string;
  telefono_uno: string;
  estado: string;
  fecha_vigencia: string;
  calle: string;
  calle_numeracion: string;
  departamento?: string;
  id_region: number;
  id_comuna: number;
  tipo: string;
  clasificacion_inscripcion: string;
  cursos: any[];
  certificado_antecedentes: string;
}

interface DatabaseTabProps {
  onViewLead?: (lead: AdministratorLead) => void;
}

const DatabaseTab: React.FC<DatabaseTabProps> = () => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [parsedData, setParsedData] = useState<MinvuRecord[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{ success: number; errors: string[] } | null>(null);

  const handleFileUpload = useCallback((info: any) => {
    const { file } = info;
    
    if (file.type !== 'application/json') {
      message.error('Please upload a JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target?.result as string);
        if (Array.isArray(jsonData)) {
          setParsedData(jsonData);
          setFileList([file]);
          message.success(`Successfully parsed ${jsonData.length} records`);
        } else {
          message.error('JSON file must contain an array of records');
        }
      } catch (error) {
        message.error('Invalid JSON file');
        console.error('JSON parsing error:', error);
      }
    };
    reader.readAsText(file);
  }, []);

  const handleImport = useCallback(async () => {
    if (parsedData.length === 0) {
      message.warning('No data to import');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    setImportResults(null);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setImportProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const results = await importMinvuData(parsedData);
      
      clearInterval(progressInterval);
      setImportProgress(100);
      setImportResults(results);
      
      if (results.success > 0) {
        message.success(`Successfully imported ${results.success} administrator leads`);
      }
      
      if (results.errors.length > 0) {
        message.warning(`${results.errors.length} records failed to import`);
      }
    } catch (error) {
      message.error('Import failed: ' + error);
    } finally {
      setIsImporting(false);
    }
  }, [parsedData]);

  const handleClear = useCallback(() => {
    setFileList([]);
    setParsedData([]);
    setImportResults(null);
    setImportProgress(0);
  }, []);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id_administrador',
      key: 'id_administrador',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      ellipsis: true,
    },
    {
      title: 'RUT',
      dataIndex: 'rut',
      key: 'rut',
      width: 120,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Phone',
      dataIndex: 'telefono_uno',
      key: 'telefono_uno',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      render: (status: string) => (
        <span style={{ color: status === 'Vigente' ? '#52c41a' : '#ff4d4f' }}>
          {status}
        </span>
      ),
    },
    {
      title: 'Region',
      dataIndex: 'id_region',
      key: 'id_region',
      width: 80,
    },
    {
      title: 'Comuna',
      dataIndex: 'id_comuna',
      key: 'id_comuna',
      width: 80,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Card title="Database Import" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={4}>Import MINVU Administrator Data</Title>
            <Text type="secondary">
              Upload JSON files containing administrator data from MINVU's condominio registry.
              The system will automatically map the data to administrator lead records.
            </Text>
            <Alert
              message="Firebase Enabled"
              description="Data will be processed and saved to the administratorLeads collection in Firebase. Batch processing will be used for optimal performance."
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Upload
                accept=".json"
                beforeUpload={() => false}
                onChange={handleFileUpload}
                fileList={fileList}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} size="large">
                  Upload JSON File
                </Button>
              </Upload>
            </Col>
            <Col span={12}>
              <Space>
                <Button
                  type="primary"
                  icon={<ImportOutlined />}
                  onClick={handleImport}
                  disabled={parsedData.length === 0 || isImporting}
                  loading={isImporting}
                >
                  Import {parsedData.length} Records
                </Button>
                <Button onClick={handleClear} disabled={parsedData.length === 0}>
                  Clear
                </Button>
              </Space>
            </Col>
          </Row>

          {isImporting && (
            <div>
              <Text>Importing administrator leads...</Text>
              <Progress percent={importProgress} status="active" />
            </div>
          )}

          {importResults && (
            <Alert
              message="Import Results"
              description={
                <div>
                  <p>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} /> 
                    {' '}Successfully imported: {importResults.success} records
                  </p>
                  {importResults.errors.length > 0 && (
                    <div>
                      <p>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> 
                        {' '}Errors: {importResults.errors.length} records
                      </p>
                      <details>
                        <summary>View errors</summary>
                        <ul style={{ marginTop: 8 }}>
                          {importResults.errors.slice(0, 10).map((error, index) => (
                            <li key={index} style={{ fontSize: '12px' }}>{error}</li>
                          ))}
                          {importResults.errors.length > 10 && (
                            <li>... and {importResults.errors.length - 10} more errors</li>
                          )}
                        </ul>
                      </details>
                    </div>
                  )}
                </div>
              }
              type={importResults.errors.length === 0 ? 'success' : 'warning'}
              showIcon
            />
          )}
        </Space>
      </Card>

      {parsedData.length > 0 && (
        <Card title={`Preview Data (${parsedData.length} records)`}>
          <Table
            columns={columns}
            dataSource={parsedData.slice(0, 10)}
            rowKey="id_administrador"
            pagination={false}
            size="small"
            scroll={{ x: 800 }}
          />
          {parsedData.length > 10 && (
            <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
              Showing first 10 records of {parsedData.length} total records
            </Text>
          )}
        </Card>
      )}

      <Card title="Data Mapping Information" style={{ marginTop: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Statistic
              title="Source"
              value="MINVU Condominios"
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Lead Type"
              value="Administrator"
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Auto-mapping"
              value="Enabled"
              prefix={<CheckCircleOutlined />}
            />
          </Col>
        </Row>
        
        <Divider />
        
        <div>
          <Title level={5}>Field Mappings</Title>
          <ul>
            <li><strong>nombre_completo</strong> → Lead Name</li>
            <li><strong>email</strong> → Primary Contact Email</li>
            <li><strong>telefono_uno</strong> → Primary Contact Phone</li>
            <li><strong>calle + calle_numeracion</strong> → Address</li>
            <li><strong>id_region</strong> → Pincode</li>
            <li><strong>id_comuna</strong> → City</li>
            <li><strong>estado + fecha_vigencia</strong> → Description</li>
            <li><strong>RUT + ID + Cursos</strong> → Notes</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default DatabaseTab;
